import { createSelector, Selector } from '@reduxjs/toolkit'
import {
  Direction,
  EntityType,
  InflightNegotiations,
  InFlightOrganisation,
  InFlightOrganisationCollateral,
  InFlightTargetOrganisationCollateral,
  NegotiationDetail,
  NegotiationStatus,
  NegotiationSubscriptionIdentifier,
  TradingEntityWithAlias
} from 'api'
import { isObject, orderBy } from 'lodash'
import { filter, map, pipe, sortBy, uniqBy, flatMapDeep } from 'lodash/fp'
import { TradingEntityWithAliasHelper } from 'model'
import { currentOrganisationIdSelector } from 'selectors/adminSelectors'
import { createCollateralByIdGetterSelector } from 'selectors/collateralSelectors'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import {
  createNegotiationDetailsSelector,
  createOrgActivityFilteredNegotiationEntrySelector,
  NegotiationFilter
} from 'selectors/negotiations'
import { hasCurrentOrganisationGridViewEnabledSelector } from 'selectors/organisationSelectors'
import { marketDetailGetterSelector } from 'selectors/platformSelectors'
import { userPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { GlobalState } from 'store'
import { createDeepEqualSelector } from 'utils/redux'

const collateralInflightNumberGetterSelector: Selector<
  GlobalState,
  (collateralId: string | null | undefined, direction: Direction | null | undefined) => number | null
> = createSelector(
  [currentOrganisationIdSelector, createEntityGetterSelector(EntityType.IN_FLIGHT_ORGANISATION), marketDetailGetterSelector],
  (organisationId, getInflightOrganisation, getMarketDetail) => {
    const inflightOrganisation: InFlightOrganisation | null = getInflightOrganisation(organisationId)

    return function getCollateralTargetInflightNumber(collateralId: string | null | undefined, direction: Direction | null | undefined) {
      if (!collateralId || !direction) return null

      const inflightCollateral: InFlightOrganisationCollateral | undefined = inflightOrganisation?.inFlightByCollateral?.[collateralId]
      const market = getMarketDetail(inflightCollateral?.market)
      const isSameMarketTradeDate = market?.tradeDate === inflightCollateral?.inFlightDate

      if (direction === Direction.BUY) {
        return isSameMarketTradeDate ? inflightCollateral?.inFlightBuy ?? 0 : 0
      }

      return isSameMarketTradeDate ? inflightCollateral?.inFlightSell ?? 0 : 0
    }
  }
)

const collateralTargetInflightNumberGetterSelector: Selector<
  GlobalState,
  (collateralId: string | null | undefined, direction: Direction | null | undefined) => number | null
> = createSelector(
  [currentOrganisationIdSelector, createEntityGetterSelector(EntityType.IN_FLIGHT_TARGET_ORGANISATION), marketDetailGetterSelector],
  (organisationId, getTargetInflightOrgansiation, getMarketDetail) => {
    const inflightOrganisation = getTargetInflightOrgansiation(organisationId)

    return function getCollateralTargetInflightNumber(collateralId: string | null | undefined, direction: Direction | null | undefined) {
      if (!collateralId || !direction) return null

      const inflightCollateral: InFlightTargetOrganisationCollateral | undefined =
        inflightOrganisation?.targetsByCollateralId?.[collateralId]

      const market = getMarketDetail(inflightCollateral?.market)
      const isSameMarketTradeDate = market?.tradeDate === inflightCollateral?.inFlightDate

      if (direction === Direction.BUY) {
        return isSameMarketTradeDate ? inflightCollateral?.targetBuy ?? 0 : 0
      }

      return isSameMarketTradeDate ? inflightCollateral?.targetSell ?? 0 : 0
    }
  }
)

const negotiationEntriesSelector = createOrgActivityFilteredNegotiationEntrySelector({
  negotiationFilter: NegotiationFilter.InitiatorOnly,
  statusSelector: createSelector([], () => [
    NegotiationStatus.ITEMS_IN_DIFFERENT_STATES,
    NegotiationStatus.REQUESTED_WITHOUT_PRICE,
    NegotiationStatus.REQUESTED_WITH_PRICE,
    NegotiationStatus.QUOTED,
    NegotiationStatus.QUOTED_SUBJECT,
    NegotiationStatus.WITHDRAWN,
    NegotiationStatus.EXPIRED,
    NegotiationStatus.REJECTED
  ]),
  hideNegotiationSentToStaging: true,
  subscriptionIdentifier: NegotiationSubscriptionIdentifier.INITIATED_GRID_VIEW
})

const negotiationDetailsSelector = createNegotiationDetailsSelector(negotiationEntriesSelector)

const pinnedCollateralsSelector = createSelector(userPreferenceSelector, userPreference => userPreference.pinnedCollaterals)

export const emptyInflightGetterSelector: Selector<GlobalState, (collateralId: string) => InflightNegotiations | null> = createSelector(
  createCollateralByIdGetterSelector,
  getCollateralById => {
    return function createEmptyInflight(collateralId: string) {
      const collateral = getCollateralById(collateralId)

      if (!collateral) return null

      return {
        id: `${collateral?.id}`,
        collateral,
        negotiationsByCounterparty: {}
      }
    }
  }
)

export const initiatedInflightDetailsSelector: Selector<GlobalState, InflightNegotiations[]> = createSelector(
  [
    hasCurrentOrganisationGridViewEnabledSelector,
    negotiationDetailsSelector,
    pinnedCollateralsSelector,
    emptyInflightGetterSelector,
    collateralInflightNumberGetterSelector,
    collateralTargetInflightNumberGetterSelector
  ],
  (isGridViewEnabled, negotiationDetails, pinnedCollaterals, createEmptyInflight, getCollateralInflightNumber, getTargetInflightNumber) => {
    if (!isGridViewEnabled) return []

    const sortedDetails: NegotiationDetail[] = orderBy(
      negotiationDetails,
      ['collateral.couponMaturityDate', 'collateral.name', 'collateral.id', 'negotiation.direction'],
      ['asc', 'asc', 'asc', 'desc']
    )

    const foundPinnedElements: NegotiationDetail[] = sortedDetails.filter(
      d => d?.collateral?.id && pinnedCollaterals.includes(d?.collateral?.id)
    )
    const notPinnedElements: NegotiationDetail[] = sortedDetails.filter(
      d => d?.collateral?.id && !pinnedCollaterals.includes(d?.collateral?.id)
    )

    const pinnedElements: (NegotiationDetail | string)[] = sortedDetails.length
      ? pinnedCollaterals.flatMap<NegotiationDetail | string>(id => {
          const found = foundPinnedElements.filter(e => e?.collateral?.id === id)
          if (found.length) return found

          return [id]
        })
      : []

    const result = [...pinnedElements, ...notPinnedElements].reduce<InflightNegotiations[]>((inflights, currentDetail) => {
      if (typeof currentDetail === 'string') {
        const emptyInflight = createEmptyInflight(currentDetail)
        if (emptyInflight) return [...inflights, emptyInflight]
        return [...inflights]
      }

      const counterpartyId: string | undefined = currentDetail?.counterparty?.id

      if (!counterpartyId) return inflights

      const idx = inflights.findIndex(i => {
        if (i.collateral.id === currentDetail.collateral?.id && i.direction === currentDetail.negotiation.direction) {
          return true
        }

        return false
      })

      if (typeof idx === 'number' && idx !== -1) {
        if (inflights[idx]?.negotiationsByCounterparty?.[counterpartyId]?.length) {
          inflights[idx].negotiationsByCounterparty[counterpartyId] = [
            ...inflights[idx].negotiationsByCounterparty[counterpartyId],
            currentDetail
          ]
        } else {
          inflights[idx].negotiationsByCounterparty[counterpartyId] = [currentDetail]
        }
      } else {
        return [
          ...inflights,
          {
            id: `${currentDetail.collateral?.id}-${currentDetail.negotiation.direction}`,
            inflight: getCollateralInflightNumber(currentDetail.collateral?.id, currentDetail.negotiation.direction),
            target: getTargetInflightNumber(currentDetail.collateral?.id, currentDetail.negotiation.direction),
            collateral: currentDetail.collateral!,
            direction: currentDetail.negotiation.direction ?? undefined,
            negotiationsByCounterparty: {
              [counterpartyId]: [currentDetail]
            }
          }
        ]
      }

      return inflights
    }, [])

    return result
  }
)

type CounterpartyColumnDetail = {
  id: string
  headerName: string
}

const mapCounterpartyToColumn = (entity: TradingEntityWithAlias): CounterpartyColumnDetail => ({
  id: TradingEntityWithAliasHelper.getId(entity),
  headerName: TradingEntityWithAliasHelper.getName(entity)
})

const isTradingEntityWithAlias = (counterparty: NegotiationDetail['counterparty']): counterparty is TradingEntityWithAlias =>
  isObject(counterparty)

const orderedNegotiationCounterPartiesSelector: Selector<GlobalState, CounterpartyColumnDetail[]> = createSelector(
  [initiatedInflightDetailsSelector],
  pipe(
    flatMapDeep(inflight => Object.values(inflight.negotiationsByCounterparty)),
    map(negotiationDetail => ({ ...negotiationDetail.counterparty, alias: negotiationDetail.negotiation.counterpartyAlias })),
    filter(isTradingEntityWithAlias),
    map(mapCounterpartyToColumn),
    sortBy(({ headerName }) => headerName),
    uniqBy(({ id }) => id)
  )
)

export const counterpartyColumnDetailSelector: Selector<GlobalState, CounterpartyColumnDetail[]> = createDeepEqualSelector(
  orderedNegotiationCounterPartiesSelector,
  counterparties => {
    return counterparties
  },
  {
    devModeChecks: {
      identityFunctionCheck: 'never',
      inputStabilityCheck: 'never'
    }
  }
)
